/**
 * Bundle 173 - Blank badge print dialog (super-admin only).
 *
 * Uses the Bundle 172 `letter` 2 x 3 pipeline with N empty-name
 * rows so admins can print a stack for walk-ins / handwritten
 * backfills. Page size, grid, borders, event text all preserved.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Printer, AlertTriangle, Download } from 'lucide-react';
import api from '@/utils/api';

const BlankBadgePrintDialog = ({ open, onClose, eventId, eventSlug }) => {
  const [count, setCount] = useState(6);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const n = Math.max(1, Math.min(60, parseInt(count, 10) || 0));
      const resp = await api.post(
        `/admin/events/${eventId}/badges/print-blank`,
        { count: n }, { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Dostination-${eventSlug || 'event'}-blank-${n}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        (detail && (detail.message || detail))
        || 'Blank badge generation failed.',
      );
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="sm:max-w-[440px]"
        data-testid="blank-badges-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5 text-primary-700" />
            Print blank badges
          </DialogTitle>
          <DialogDescription>
            Same 2 x 3 US Letter grid as the regular badge sheet, with
            an empty name area on each slot for walk-ins and
            handwritten backfills. 6 per page.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <label
              htmlFor="blank-count"
              className="block text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1"
            >
              How many blank badges? (1 to 60)
            </label>
            <Input
              id="blank-count"
              type="number"
              min={1}
              max={60}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              data-testid="blank-badges-count"
            />
            <div className="text-xs text-neutral-500 mt-1">
              6 badges per page. {Math.ceil(
                Math.max(1, Math.min(60, parseInt(count, 10) || 0)) / 6,
              )}{' '}
              page(s) total.
            </div>
          </div>
          {error ? (
            <div
              className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-2 text-red-900"
              role="alert"
              data-testid="blank-badges-error"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="text-xs">{error}</div>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            data-testid="blank-badges-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="blank-badges-confirm"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-1" />
            )}
            Generate PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BlankBadgePrintDialog;
